import {useEffect, useState} from 'react';
import style from './index.module.scss';
import {
    Button,
    Col,
    Empty,
    Form,
    Input,
    message,
    ModalProps,
    Row,
    Select,
} from 'antd';
import {useForm} from 'antd/es/form/Form';
import {DeleteTwoTone, PlusSquareOutlined} from '@ant-design/icons';
import useModalHandler from 'Utils/Hooks/useModalHandler';
import ParamsShowModal from './Components/ParamsShowModal';
import {useAtomValue, useSetAtom} from 'jotai';
import useRequestFn from 'Utils/Hooks/useRequestFn';
import {errorMessage} from 'Utils/common';
import {paramsInfoAtom} from '../../store';
import {
    getParamsJson,
    mergeObj,
    ParamsInfo,
    saveTask,
    TaskListItem,
    TaskSaveParams,
} from '../../../../../service';
import {serviceListAtom} from '../../../../../store';
import _ from 'lodash-es';
import {ProModal} from '../../../../../../../Components/ProModal';

interface AddPlanProps extends ModalProps {
    initValue?: TaskListItem; //当前计划列表数据
    updateData: () => void; //更新函数
    flag?: 'add' | 'edit';
}

interface service {
    serviceId: string;
    params: {[key: string]: string};
}

export default function AddTaskModal({
    initValue,
    updateData,
    flag = 'add',
    ...restProps
}: AddPlanProps) {
    const {Option} = Select;
    const [form] = useForm();
    const modal = useModalHandler();

    const [paramsList, setParamsList] = useState<{
        [key: number]: ParamsInfo[] | undefined;
    }>();
    const serviceList = useAtomValue(serviceListAtom);
    const setParamsInfo = useSetAtom(paramsInfoAtom);

    const [save] = useRequestFn(saveTask);

    useEffect(() => {
        const list = form
            .getFieldValue('service')
            ?.filter((item: service) => item?.serviceId)
            .map((item: service) =>
                _.keys(item.params).map(key => ({
                    name: key,
                    value: item.params[key],
                })),
            );

        setParamsList({...paramsList, ...list});
    }, [initValue]);

    async function handleFinish(data: TaskSaveParams) {
        const params = {
            ...data,
            id: flag === 'edit' ? initValue?.id : '',
        };

        try {
            await save({...params});
            message.success(flag === 'add' ? '添加成功' : '修改成功');
            updateData();
        } catch (e) {
            message.error(errorMessage(e));
        }
    }

    function onSelectChange(e: string, key: number, name: number) {
        //切换业务服务时获取对应的参数列表
        const params = getParamsJson(serviceList, e);
        setParamsList({...paramsList, [key]: params});

        //获取初始值serviceList
        const initServiceList = initValue?.serviceList;
        //判断当前选中项是否有初始值
        const hasVal = initServiceList?.[key]?.serviceId === e;
        //如果有初始值，则获取初始值,否则值为空
        const value = hasVal
            ? initServiceList?.find(item => item.serviceId === e)?.params
            : mergeObj(
                  params?.map(item => ({[item.name]: item.value || ''})) || [],
              );
        //使用 form.setFieldsValue 来动态改变表单值
        const newData = form.getFieldValue('service');
        newData[name] = {
            serviceId: e,
            ...value,
        };

        form.setFieldsValue({
            service: newData,
        });
    }

    function onOpenInfo(name: number) {
        //获取当前项id
        const id = form.getFieldValue('service')[name]?.serviceId;
        //根据id获取参数列表
        const params = getParamsJson(serviceList, id);
        setParamsInfo(params);
        modal.open();
    }

    const layout = {
        labelCol: {span: 4},
        wrapperCol: {span: 17, offset: 1},
    };

    return (
        <ProModal.Large
            title={flag === 'add' ? '新增调度任务' : '编辑调度任务'}
            maskClosable={false}
            onOk={form.submit}
            {...restProps}
            width={880}
            className={style.root}>
            <div className={style.over}>
                <Form
                    form={form}
                    onFinish={handleFinish}
                    className={style.form}
                    labelCol={layout.labelCol}
                    wrapperCol={layout.wrapperCol}
                    scrollToFirstError
                    autoComplete="off"
                    initialValues={{
                        service:
                            flag === 'add'
                                ? [{serviceId: null}]
                                : initValue!.serviceList,
                    }}>
                    <Form.Item
                        name="name"
                        label="名称："
                        initialValue={flag === 'add' ? '' : initValue?.name}>
                        <Input placeholder="输入调度任务名称" maxLength={500} />
                    </Form.Item>

                    <Form.Item
                        name="code"
                        label="编码"
                        initialValue={flag === 'add' ? '' : initValue?.code}
                        rules={[
                            {
                                required: true,
                                message: `请输入调度任务名称的半角英文编码`,
                            },
                        ]}>
                        <Input
                            placeholder="输入调度任务名称的半角英文编码"
                            maxLength={200}
                            disabled={flag === 'edit'}
                        />
                    </Form.Item>

                    <Form.Item
                        name="status"
                        label="启用状态"
                        initialValue={flag === 'add' ? '0' : initValue?.status}>
                        <Select
                            value="0"
                            placeholder="请选择启用状态"
                            dropdownClassName={style.dropdown}>
                            <Option value="1">是</Option>
                            <Option value="0">否</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="业务服务："
                        required
                        className={style.plan}>
                        <Form.List name="service">
                            {(fields, {add, remove}) => (
                                <>
                                    {fields.map(
                                        ({key, name, ...restField}, index) => (
                                            <div
                                                key={key}
                                                className={style.planContainer}>
                                                <Form.Item
                                                    label="选择服务："
                                                    name={[name, 'serviceId']}
                                                    {...restField}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message:
                                                                '请选择业务服务',
                                                        },
                                                    ]}>
                                                    <Select
                                                        placeholder="请选择业务服务"
                                                        dropdownClassName={
                                                            style.dropdown
                                                        }
                                                        onChange={(e: string) =>
                                                            onSelectChange(
                                                                e,
                                                                key,
                                                                name,
                                                            )
                                                        }>
                                                        {serviceList.map(
                                                            item => (
                                                                <Option
                                                                    value={
                                                                        item.id
                                                                    }
                                                                    key={
                                                                        item.id
                                                                    }>
                                                                    {item.name}
                                                                </Option>
                                                            ),
                                                        )}
                                                    </Select>
                                                </Form.Item>

                                                <div className={style.params}>
                                                    <div
                                                        className={
                                                            style.planRules
                                                        }>
                                                        输入参数：
                                                    </div>
                                                    <div
                                                        className={
                                                            style.wrapper
                                                        }>
                                                        <Row
                                                            className={
                                                                style.row
                                                            }>
                                                            <Col
                                                                className={
                                                                    style.paramsName
                                                                }>
                                                                参数名称
                                                            </Col>
                                                            <Col
                                                                className={
                                                                    style.paramsValue
                                                                }>
                                                                参数值
                                                            </Col>
                                                            <Col
                                                                className={
                                                                    style.tips
                                                                }
                                                                onClick={() =>
                                                                    onOpenInfo(
                                                                        name,
                                                                    )
                                                                }>
                                                                参数说明
                                                            </Col>
                                                        </Row>

                                                        {!paramsList?.[key] && (
                                                            <Empty />
                                                        )}

                                                        {paramsList?.[key]?.map(
                                                            item => (
                                                                <Form.Item
                                                                    key={
                                                                        item.name
                                                                    }
                                                                    label={
                                                                        item.name
                                                                    }
                                                                    className={
                                                                        style.paramsItem
                                                                    }
                                                                    name={[
                                                                        name,
                                                                        item.name,
                                                                    ]}
                                                                    {...restField}
                                                                    initialValue={
                                                                        item.value ||
                                                                        ''
                                                                    }
                                                                    preserve={
                                                                        false
                                                                    }
                                                                    shouldUpdate>
                                                                    <Input
                                                                        placeholder={`请输入${item.name}`}
                                                                        maxLength={
                                                                            200
                                                                        }
                                                                        bordered={
                                                                            false
                                                                        }
                                                                        key={Math.random()}
                                                                    />
                                                                </Form.Item>
                                                            ),
                                                        )}
                                                    </div>
                                                </div>

                                                {fields.length > 1 ? (
                                                    <DeleteTwoTone
                                                        className={
                                                            style.deleteBtn
                                                        }
                                                        onClick={() =>
                                                            remove(name)
                                                        }
                                                    />
                                                ) : null}
                                            </div>
                                        ),
                                    )}
                                    <Button
                                        onClick={() => add()}
                                        icon={<PlusSquareOutlined />}
                                        className={style.addPlanBtn}>
                                        添加服务
                                    </Button>
                                </>
                            )}
                        </Form.List>
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="说明"
                        initialValue={
                            flag === 'add' ? '' : initValue?.description
                        }>
                        <Input placeholder="输入说明" maxLength={500} />
                    </Form.Item>
                </Form>
            </div>

            {modal.render(
                <ParamsShowModal onCancel={modal.close} open={modal.visible} />,
            )}
        </ProModal.Large>
    );
}
